import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { processService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';
import { formatReqDate } from '../../utils/requerimentoStatus';

function papelLabel(papel) {
  return papel === 'requerente' ? 'Requerente' : 'Órgão';
}

function MensagemCard({ message, editable, onSave, saving }) {
  const [texto, setTexto] = useState(message.texto || '');
  const dirty = texto.trim() !== (message.texto || '').trim();

  return (
    <div className={`rounded-lg px-3 py-2 text-sm ${message.autor_papel === 'orgao' ? 'bg-amber-50' : 'bg-slate-50'}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="font-semibold text-slate-700">{papelLabel(message.autor_papel)}</span>
        <span>{editable ? 'Pode ajustar' : message.editavel ? 'Aguardando envio' : 'Enviada'}</span>
        {message.created_at ? <span>{formatReqDate(message.created_at)}</span> : null}
      </div>
      {editable ? (
        <div className="mt-2">
          <textarea
            className="form-input min-h-[72px]"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-outline mt-2"
            disabled={!dirty || texto.trim().length < 1 || saving}
            onClick={() => onSave(message.id, texto.trim())}
          >
            Salvar ajuste
          </button>
        </div>
      ) : (
        <p className="mt-1 whitespace-pre-wrap text-slate-800">{message.texto}</p>
      )}
    </div>
  );
}

export default function ChecklistThread({ reqId, item, status, requester, userId }) {
  const queryClient = useQueryClient();
  const papel = requester ? 'requerente' : 'orgao';
  const myTurn = requester
    ? status === 'devolvido'
    : status === 'enviado' || status === 'desbloqueio_solicitado';
  const messages = (Array.isArray(item?.mensagens) ? item.mensagens : []).filter(
    (message) => message.autor_papel === papel || !message.editavel
  );
  const [draft, setDraft] = useState('');

  const refresh = () => queryClient.invalidateQueries(['requerimento', reqId]);

  const postMut = useMutation((texto) => processService.criarMensagemChecklist(reqId, item.id, texto), {
    onSuccess: () => {
      setDraft('');
      toast.success('Mensagem publicada');
      refresh();
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });
  const patchMut = useMutation(
    ({ mensagemId, texto }) => processService.atualizarMensagemChecklist(reqId, mensagemId, texto),
    {
      onSuccess: () => {
        toast.success('Mensagem ajustada');
        refresh();
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  if (!messages.length && !myTurn) return null;

  return (
    <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Apontamentos</p>
      {messages.map((message) => {
        const mine = message.autor_papel === papel && (!message.autor_id || message.autor_id === userId);
        return (
          <MensagemCard
            key={message.id}
            message={message}
            editable={Boolean(myTurn && message.editavel && mine)}
            saving={patchMut.isLoading}
            onSave={(mensagemId, texto) => patchMut.mutate({ mensagemId, texto })}
          />
        );
      })}
      {myTurn ? (
        <div>
          <textarea
            className="form-input min-h-[72px]"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={
              requester
                ? 'Responda ou questione o apontamento deste item'
                : 'Descreva o que precisa ser ajustado neste item'
            }
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">
              Dá para ajustar enquanto o pedido não for enviado. Depois do envio, a mensagem fica registrada e só é possível acrescentar outra.
            </p>
            <button
              type="button"
              className="btn btn-outline"
              disabled={draft.trim().length < 1 || postMut.isLoading}
              onClick={() => postMut.mutate(draft.trim())}
            >
              Publicar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
